import { Button, Stack, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import invoiceLogo from "../../../assets/images/invoicing-icon.svg";
import CommercialAirlineNavigationBar from "../../../components/element/CommercialAirlineNavigationBar";
import useAirlineAdminApi from "../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../services/StorageProvider";
import ROUTE_PATHS from "../../../utils/routePaths";
import InvoicingIATAFilter from "./Views/InvoicingIATAFilter";
import { IATA_INVOICE_STATUS } from "../../../utils/constants";
import dayjs from "dayjs";
import NewInvoicingIATATable from "./Tables/NewInvoicingIATATable";
import PaidInvoicingIATATable from "./Tables/PaidInvoicingIATATable";

function InvoicingIATAScreen() {
  const navigate = useNavigate();
  const { fetchIATAInvoices } = useAirlineAdminApi();
  const { associatedAirlineData } = useContext(StorageContext);
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState("");
  const [filterInvoiceDate, setFilterInvoiceDate] = useState(null);
  const [selectedTab, setSelectedTab] = useState(() => {
    const savedTab = localStorage.getItem("selectedIATAInvoiceTab");
    return savedTab ? parseInt(savedTab, 10) : 1;
  });

  const {
    data: iataInvoicesResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "get-iata-invoices",
      selectedAirline?.id,
      selectedTab,
      filterInvoiceNumber,
      filterInvoiceDate,
    ],
    queryFn: () =>
      fetchIATAInvoices(selectedAirline?.id, {
        status: getStatusForTab(),
        ...(filterInvoiceNumber !== ""
          ? { invoiceNumber: filterInvoiceNumber }
          : {}),
        ...(filterInvoiceDate
          ? {
            invoiceDate: dayjs(filterInvoiceDate).format("YYYY-MM-DD"),
          }
          : {}),
      }),
    enabled: !!selectedAirline,
  });

  const getStatusForTab = () => {
    switch (selectedTab) {
      case 1:
        return IATA_INVOICE_STATUS.NEW_INVOICE;
      case 2:
        return IATA_INVOICE_STATUS.PAID;
      case 3:
        return IATA_INVOICE_STATUS.PENDING;
      case 4:
        return IATA_INVOICE_STATUS.OVERDUE;
      default:
        return IATA_INVOICE_STATUS.NEW_INVOICE;
    }
  };

  const tabIsActive = (tabId) => {
    return selectedTab === tabId;
  };

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    localStorage.setItem("selectedIATAInvoiceTab", tabId.toString());
  };

  useEffect(() => {
    if (associatedAirlineData?.data?.length > 0) {
      setSelectedAirline(associatedAirlineData?.data[0]);
    }
  }, [associatedAirlineData]);

  const handleAirlineChange = (event) => {
    const selectedId = event.target.value;
    const selectedAirline = associatedAirlineData?.data?.find(
      (airline) => airline.id === selectedId
    );
    setSelectedAirline(selectedAirline);
  };

  const handleViewInvoiceClick = (item) => {
    localStorage.setItem("iataInvoicingData", JSON.stringify(item));
    navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICING_IATA_VIEW);
  };

  const handleViewPaymentRecieptClick = (item) => {
    localStorage.setItem("iataInvoicingData", JSON.stringify(item));
    navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICING_IATA_PAYMENT_RECIEPT);
  };

  const handleApplyFilter = (invoiceNumber, invoiceDate) => {
    setFilterInvoiceNumber(invoiceNumber);
    setFilterInvoiceDate(invoiceDate);
  };

  const handleClearFilter = () => {
    setFilterInvoiceNumber("");
    setFilterInvoiceDate(null);
  };

  const getInvoicingTable = () => {
    if (selectedTab === 2 || selectedTab === 3) {
      return (
        <PaidInvoicingIATATable
          invoicingData={iataInvoicesResponse?.data}
          isLoading={isLoading}
          error={error}
          onClickView={handleViewInvoiceClick}
          onClickViewPayment={handleViewPaymentRecieptClick}
        />
      );
    } else {
      return (
        <NewInvoicingIATATable
          invoicingData={iataInvoicesResponse?.data}
          isLoading={isLoading}
          error={error}
          onClickView={handleViewInvoiceClick}
        />
      );
    }
  };

  return (
    <div>
      <div className="invoicing-IATA-container">
        <CommercialAirlineNavigationBar
          title={"Invoices"}
          fullWidthContent={true}
          navigationLogo={invoiceLogo}
          fullWidthContent={true}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              IATA Invoices
            </div>
          }
          airlineData={associatedAirlineData}
          onChangeAirline={handleAirlineChange}
          selectedAirline={selectedAirline}
        />
        <div className="invoicing-IATA-tab-container">
          <Stack direction="row" spacing={1}>
            <Tooltip
              title="Invoices that have been generated but not yet marked as paid."
              arrow
              placement="bottom"
            >
              <Button
                variant="contained"
                className={tabIsActive(1) ? "active" : ""}
                onClick={() => handleTabChange(1)}
              >
                New Invoice
              </Button>
            </Tooltip>
            <Tooltip
              title="Invoices that have been fully paid by the user."
              arrow
              placement="bottom"
            >
              <Button
                variant="contained"
                className={tabIsActive(2) ? "active" : ""}
                onClick={() => handleTabChange(2)}
              >
                Paid
              </Button>
            </Tooltip>
            <Tooltip
              title="Payment from customer pending reconciliation with corresponding invoices are listed here."
              arrow
              placement="bottom"
            >
              <Button
                variant="contained"
                className={tabIsActive(3) ? "active" : ""}
                onClick={() => handleTabChange(3)}
              >
                Verification Pending
              </Button>
            </Tooltip>
            <Tooltip
              title="Invoices past their due date that are still unpaid."
              arrow
              placement="bottom"
            >
              <Button
                variant="contained"
                className={tabIsActive(4) ? "active" : ""}
                onClick={() => handleTabChange(4)}
              >
                Overdue
              </Button>
            </Tooltip>
          </Stack>
        </div>
        <div className="invoicing-IATA-container-divider"></div>

        <InvoicingIATAFilter
          onClickApply={handleApplyFilter}
          onClickClear={handleClearFilter}
        />
        {getInvoicingTable()}
      </div>
    </div>
  );
}

export default InvoicingIATAScreen;
